import { FieldErrors } from 'react-hook-form'
import { toast } from 'react-toastify'

/**
 * Extracts the first error message from a deeply nested RHF errors object.
 * Handles arrays (items, contacts, discounts), nested objects (payment_type_details),
 * and root-level errors from Yup .test() validators.
 */
export function extractFirstErrorMessage(
  errors: FieldErrors
): string | undefined {
  const RHF_META_KEYS = new Set(['ref', 'type', 'types', 'root'])

  function traverse(obj: unknown): string | undefined {
    if (!obj || typeof obj !== 'object') return undefined

    const record = obj as Record<string, unknown>

    // If this node has a `message` property that is a non-empty string, it's a leaf error
    if (typeof record.message === 'string' && record.message.length > 0) {
      return record.message
    }

    // Check `root` first — Yup .test() on arrays/objects produces root-level errors
    if (record.root && typeof record.root === 'object') {
      const rootMsg = traverse(record.root)
      if (rootMsg) return rootMsg
    }

    // Iterate own keys (skip RHF metadata)
    for (const key of Object.keys(record)) {
      if (RHF_META_KEYS.has(key)) continue

      const value = record[key]

      if (Array.isArray(value)) {
        for (const item of value) {
          const msg = traverse(item)
          if (msg) return msg
        }
      } else if (value && typeof value === 'object') {
        const msg = traverse(value)
        if (msg) return msg
      }
    }

    return undefined
  }

  return traverse(errors)
}

/**
 * Shows a toast with the first validation error message.
 * Designed to be used as the onError callback of handleSubmit,
 * which guarantees it only fires on submit — never on onChange/onBlur.
 *
 * Usage:
 * ```ts
 * import { toastFirstError } from 'utils/yupResolverWithToast'
 *
 * handleSubmit(onValid, toastFirstError)(e)
 * ```
 */
export function toastFirstError(errors: FieldErrors): void {
  const message = extractFirstErrorMessage(errors)
  if (message) {
    toast.error(message)
    return
  }
  // SC-10012: erro sem texto deixava o clique em "Salvar" sem nenhuma reação
  if (errors && Object.keys(errors).length > 0) {
    toast.error('Revise os campos do formulário antes de salvar.')
  }
}
